import { useEffect } from 'react';

interface SEOConfig {
  /** <title> único por página. Nunca repetir entre rutas. */
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  /** Ruta que empieza con "/" — usada para canonical y og:url. */
  canonicalPath?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

export const BASE_URL = 'https://www.cabinadefotos.com.co';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const PAGE_LD_ID = 'page-json-ld';

function setMeta(name: string, content: string, attr: 'name' | 'property' = 'name') {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setLink(rel: string, href: string) {
  let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export function useSEO({ title, description, keywords, ogImage, canonicalPath, jsonLd }: SEOConfig) {
  const ld = jsonLd ? JSON.stringify(jsonLd) : undefined;

  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const image = ogImage
      ? ogImage.startsWith('http')
        ? ogImage
        : `${BASE_URL}${ogImage}`
      : DEFAULT_OG_IMAGE;
    const url = canonicalPath ? `${BASE_URL}${canonicalPath}` : BASE_URL;

    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:image', image, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('og:url', url, 'property');
    setMeta('og:site_name', 'Cabina de Fotos', 'property');
    setMeta('og:locale', 'es_CO', 'property');

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);

    setLink('canonical', url);

    let script = document.getElementById(PAGE_LD_ID) as HTMLScriptElement | null;
    if (ld) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = PAGE_LD_ID;
        document.head.appendChild(script);
      }
      script.textContent = ld;
    } else {
      script?.remove();
    }

    return () => {
      document.getElementById(PAGE_LD_ID)?.remove();
    };
  }, [title, description, keywords, ogImage, canonicalPath, ld]);
}

/** Breadcrumbs para resultados de Google. */
export function breadcrumbLd(items: { name: string; path: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`,
    })),
  };
}

/** FAQPage listo para pasar a useSEO. */
export function faqLd(faqs: { q: string; a: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}
